import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader } from '@/components/app/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { StatusPill } from '@/components/app/StatusPill';
import { formatINR, fmtDate, periodLabel } from '@/lib/format';
import { Receipt as ReceiptIcon, FileText } from 'lucide-react';
import { toast } from 'sonner';

export default function TenantPayments() {
  const [demands, setDemands] = useState([]);
  const [payments, setPayments] = useState([]);
  const [receipt, setReceipt] = useState(null);
  const load = () => Promise.all([
    api.get('/rent-demands').then(r => setDemands(r.data)),
    api.get('/payments').then(r => setPayments(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const openReceipt = async (id) => {
    const r = await api.get(`/payments/${id}/receipt`);
    setReceipt(r.data);
  };

  return (
    <div>
      <PageHeader title="Rent &amp; payments" subtitle="Demands, history, and downloadable receipts" />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
        <Card className="lg:col-span-7 p-0 overflow-x-auto">
          <div className="px-4 py-3 border-b text-sm font-semibold">Rent demands</div>
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-muted/60"><tr><th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Period</th><th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Due</th><th className="text-right px-3 py-2 text-xs uppercase text-muted-foreground">Amount</th><th className="text-right px-3 py-2 text-xs uppercase text-muted-foreground">Outstanding</th><th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Status</th></tr></thead>
            <tbody>
              {demands.map((d) => (
                <tr key={d.id} className="border-t hover:bg-muted/40">
                  <td className="px-3 py-2">{periodLabel(d.period)}</td>
                  <td className="px-3 py-2">{fmtDate(d.due_date)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatINR(d.amount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{formatINR(d.outstanding)}</td>
                  <td className="px-3 py-2"><StatusPill status={d.status} /></td>
                </tr>
              ))}
              {!demands.length && <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No demands.</td></tr>}
            </tbody>
          </table>
        </Card>
        <Card className="lg:col-span-5 p-0 overflow-hidden">
          <div className="px-4 py-3 border-b text-sm font-semibold flex items-center gap-2"><ReceiptIcon className="h-4 w-4" /> Receipts</div>
          <div className="divide-y">
            {payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between p-3 text-sm">
                <div>
                  <div className="font-mono text-xs">{p.receipt_number}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(p.payment_date)} · <span className="uppercase">{p.mode}</span></div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="tabular-nums font-medium">{formatINR(p.amount)}</span>
                  <Button size="sm" variant="outline" onClick={() => openReceipt(p.id)} data-testid={`view-receipt-${p.id}`}><FileText className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}
            {!payments.length && <div className="p-8 text-center text-muted-foreground text-sm">No receipts.</div>}
          </div>
        </Card>
      </div>

      <Dialog open={!!receipt} onOpenChange={(v) => !v && setReceipt(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="h-4 w-4" /> Receipt</DialogTitle></DialogHeader>
          {receipt && (
            <div className="text-sm border rounded-lg p-4 space-y-2">
              <div className="flex justify-between"><span className="text-muted-foreground">Receipt #</span><span className="font-mono">{receipt.payment.receipt_number}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Date</span><span>{fmtDate(receipt.payment.payment_date)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Tenant</span><span>{receipt.tenant?.name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Property</span><span>{receipt.property?.name} · {receipt.unit?.unit_number}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Mode</span><span className="uppercase">{receipt.payment.mode}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Reference</span><span className="font-mono text-xs">{receipt.payment.reference || '—'}</span></div>
              <div className="border-t pt-2 mt-2 flex justify-between text-base font-semibold"><span>Amount</span><span className="tabular-nums">{formatINR(receipt.payment.amount)}</span></div>
              <Button className="w-full mt-3" onClick={() => window.print()}>Print</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
